import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { surveysApi } from '../api'
import type { SurveyEquipmentDetail, SurveyEquipmentUpdate, SurveySiteDetail } from '../types'

const EQUIPMENT_TYPES = ['PLC', '온습도계', '카운터', 'CNC', '인버터', '전력량계', '기타']

/** 현장 설비 목록을 {현장명}_설비조사.xlsx로 내려받는다. exceljs는 무거워 동적 import로 분리 로드. */
async function exportSiteXlsx(site: SurveySiteDetail) {
  const { Workbook } = await import('exceljs')
  const wb = new Workbook()
  const ws = wb.addWorksheet('설비조사')
  ws.columns = [
    { header: '구분', width: 14 },
    { header: '설비명', width: 24 },
    { header: '장비 타입(PLC의 경우 PLC/CPU)', width: 32 },
    { header: '추가설치', width: 24 },
    { header: '기타', width: 48 },
  ]
  ws.getRow(1).font = { bold: true }
  for (const e of site.equipments) {
    const type = e.equipmentType === 'PLC'
      ? [e.plcBrand, e.plcCpuType].filter(Boolean).join('/') || 'PLC'
      : e.equipmentType === '기타' && e.equipmentTypeEtc
        ? `기타(${e.equipmentTypeEtc})`
        : e.equipmentType
    const addons = [
      e.addonPower && '전원',
      e.addonHub && '허브',
      e.addonEthernetCard && '이더넷카드',
    ].filter(Boolean).join(', ')
    ws.addRow([e.category, e.name, type, addons, e.note])
  }
  const buffer = await wb.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${site.name.replace(/[\\/:*?"<>|]/g, '_')}_설비조사.xlsx`
  a.click()
  URL.revokeObjectURL(url)
}

/** 사진은 인증이 필요해 blob으로 받아 object URL로 렌더링한다.
 *  캐시에는 Blob만 두고 object URL은 마운트마다 생성/해제한다 —
 *  URL을 캐시에 넣으면 언마운트 시 revoke된 URL이 캐시에 남아 재방문 때 사진이 깨진다. */
function usePhotoUrl(photoId: string) {
  const { data: blob } = useQuery({
    queryKey: ['survey-photo', photoId],
    queryFn: () => surveysApi.getPhoto(photoId).then(r => r.data as Blob),
    staleTime: Infinity,
  })
  const url = useMemo(() => (blob ? URL.createObjectURL(blob) : undefined), [blob])
  useEffect(() => () => { if (url) URL.revokeObjectURL(url) }, [url])
  return url
}

function SurveyPhoto({ photoId, onClick }: { photoId: string; onClick?: () => void }) {
  const url = usePhotoUrl(photoId)
  if (!url) return <div className="w-20 h-20 rounded-lg bg-gray-100 animate-pulse" />
  return (
    <img src={url} alt="설비 사진" onClick={onClick}
      className="w-20 h-20 rounded-lg object-cover border border-gray-200 cursor-zoom-in hover:opacity-80 transition" />
  )
}

function equipmentToForm(e: SurveyEquipmentDetail): SurveyEquipmentUpdate {
  return {
    category: e.category, name: e.name,
    equipmentType: e.equipmentType, equipmentTypeEtc: e.equipmentTypeEtc,
    plcBrand: e.plcBrand, plcCpuType: e.plcCpuType,
    addonPower: e.addonPower, addonHub: e.addonHub, addonEthernetCard: e.addonEthernetCard,
    note: e.note,
  }
}

function EquipmentCard({ equipment, siteId }: { equipment: SurveyEquipmentDetail; siteId: string }) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<SurveyEquipmentUpdate>(() => equipmentToForm(equipment))
  const [zoomPhotoId, setZoomPhotoId] = useState<string | null>(null)

  const invalidate = () => qc.invalidateQueries({ queryKey: ['surveys', siteId] })

  const updateMutation = useMutation({
    mutationFn: () => surveysApi.updateEquipment(equipment.id, form),
    onSuccess: () => { invalidate(); setEditing(false) },
  })
  const reimportMutation = useMutation({
    mutationFn: () => surveysApi.reimportEquipment(equipment.id),
    onSuccess: () => { invalidate(); setEditing(false) },
  })
  const deleteMutation = useMutation({
    mutationFn: () => surveysApi.deleteEquipment(equipment.id),
    onSuccess: invalidate,
  })

  const startEdit = () => { setForm(equipmentToForm(equipment)); setEditing(true) }
  const isPlc = form.equipmentType === 'PLC'
  const isEtc = form.equipmentType === '기타'

  const typeLabel = equipment.equipmentType === '기타' && equipment.equipmentTypeEtc
    ? `기타(${equipment.equipmentTypeEtc})`
    : equipment.equipmentType

  const addons = [
    equipment.addonPower && '전원 추가',
    equipment.addonHub && '허브 추가',
    equipment.addonEthernetCard && '이더넷카드 추가',
  ].filter(Boolean).join(' · ')

  return (
    <div className="bg-surface rounded-xl border border-gray-200 p-5">
      {/* 헤더 행 */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {equipment.category && (
              <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-500">{equipment.category}</span>
            )}
            <span className="font-semibold text-gray-800">{equipment.name || '이름 없는 설비'}</span>
            <span className="px-2 py-0.5 rounded text-xs font-mono border border-gray-200 text-gray-500">{typeLabel}</span>
            {equipment.isEdited && (
              <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">수정됨</span>
            )}
            {equipment.hasNewerUpload && (
              <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">새 업로드 있음</span>
            )}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            {equipment.equipmentType === 'PLC' && (equipment.plcBrand || equipment.plcCpuType) && (
              <span className="mr-3">{equipment.plcBrand} {equipment.plcCpuType}</span>
            )}
            {addons && <span className="mr-3">{addons}</span>}
            {equipment.clientUpdatedAt && (
              <span>조사 {new Date(equipment.clientUpdatedAt).toLocaleString('ko-KR')}</span>
            )}
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          {equipment.hasNewerUpload && (
            <button onClick={() => {
              if (confirm('최신 업로드 내용으로 되돌립니다. 수동 수정한 내용은 사라집니다. 계속할까요?'))
                reimportMutation.mutate()
            }}
              className="border border-amber-300 text-amber-700 px-3 py-1.5 rounded-lg text-xs hover:bg-amber-50 transition">
              새 업로드 가져오기
            </button>
          )}
          {!editing && (
            <button onClick={startEdit}
              className="border border-gray-300 text-gray-600 px-3 py-1.5 rounded-lg text-xs hover:bg-gray-50 transition">
              수정
            </button>
          )}
          <button onClick={() => {
            if (confirm(`'${equipment.name || '이름 없는 설비'}'를 삭제할까요?`))
              deleteMutation.mutate()
          }}
            className="border border-red-200 text-red-600 px-3 py-1.5 rounded-lg text-xs hover:bg-red-50 transition">
            삭제
          </button>
        </div>
      </div>

      {/* 비고 (읽기 모드) */}
      {!editing && equipment.note && (
        <p className="mt-3 text-sm text-gray-600 whitespace-pre-wrap bg-gray-50 rounded-lg px-3 py-2">{equipment.note}</p>
      )}

      {/* 편집 폼 */}
      {editing && (
        <div className="mt-4 border-t border-gray-100 pt-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">구분</label>
              <input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">설비명</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">장비 타입</label>
              <select value={form.equipmentType}
                onChange={e => setForm({ ...form, equipmentType: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-surface">
                {EQUIPMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            {isEtc && (
              <div>
                <label className="block text-xs text-gray-500 mb-1">기타 장비 이름</label>
                <input value={form.equipmentTypeEtc} onChange={e => setForm({ ...form, equipmentTypeEtc: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
            )}
            {isPlc && (
              <>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">PLC 종류</label>
                  <input value={form.plcBrand} onChange={e => setForm({ ...form, plcBrand: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">CPU 타입</label>
                  <input value={form.plcCpuType} onChange={e => setForm({ ...form, plcCpuType: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              </>
            )}
          </div>
          <div className="flex gap-4 mt-3 text-sm text-gray-600">
            <label className="flex items-center gap-1.5">
              <input type="checkbox" checked={form.addonPower}
                onChange={e => setForm({ ...form, addonPower: e.target.checked })} /> 전원 추가
            </label>
            <label className="flex items-center gap-1.5">
              <input type="checkbox" checked={form.addonHub}
                onChange={e => setForm({ ...form, addonHub: e.target.checked })} /> 허브 추가
            </label>
            {isPlc && (
              <label className="flex items-center gap-1.5">
                <input type="checkbox" checked={form.addonEthernetCard}
                  onChange={e => setForm({ ...form, addonEthernetCard: e.target.checked })} /> 이더넷카드 추가
              </label>
            )}
          </div>
          <div className="mt-3">
            <label className="block text-xs text-gray-500 mb-1">기타</label>
            <textarea value={form.note} onChange={e => setForm({ ...form, note: e.target.value })}
              rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={() => updateMutation.mutate()} disabled={updateMutation.isPending}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-accent-hover transition disabled:opacity-50">
              저장
            </button>
            <button onClick={() => setEditing(false)}
              className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 transition">
              취소
            </button>
          </div>
        </div>
      )}

      {/* 사진 */}
      {equipment.photos.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {equipment.photos.map(p => (
            <SurveyPhoto key={p.id} photoId={p.id} onClick={() => setZoomPhotoId(p.id)} />
          ))}
        </div>
      )}

      {/* 사진 확대 모달 */}
      {zoomPhotoId && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-8"
          onClick={() => setZoomPhotoId(null)}>
          <ZoomedPhoto photoId={zoomPhotoId} />
        </div>
      )}
    </div>
  )
}

function ZoomedPhoto({ photoId }: { photoId: string }) {
  const url = usePhotoUrl(photoId)
  if (!url) return <div className="text-white text-sm">불러오는 중…</div>
  return <img src={url} alt="설비 사진 원본" className="max-w-full max-h-full rounded-lg" />
}

export default function SurveyDetail() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [name, setName] = useState('')

  const { data: site } = useQuery({
    queryKey: ['surveys', id],
    queryFn: () => surveysApi.getSite(id).then(r => r.data),
    enabled: !!id,
  })

  useEffect(() => { if (site) setName(site.name) }, [site])

  const renameMutation = useMutation({
    mutationFn: () => surveysApi.renameSite(id, name),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['surveys'] })
      qc.invalidateQueries({ queryKey: ['surveys', id] })
    },
  })
  const deleteMutation = useMutation({
    mutationFn: () => surveysApi.deleteSite(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['surveys'] })
      navigate('/surveys')
    },
  })

  if (!site) return <div className="text-gray-400 text-sm">불러오는 중…</div>

  const dirty = name.trim() !== site.name

  return (
    <div>
      <button onClick={() => navigate('/surveys')} className="text-sm text-gray-400 hover:text-gray-600 transition mb-4">
        ← 설비조사 목록
      </button>

      <div className="flex items-center justify-between mb-6 gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <input value={name} onChange={e => setName(e.target.value)}
            className="text-xl font-semibold tracking-tight text-gray-900 bg-transparent border-b-2 border-transparent hover:border-gray-200 focus:border-blue-500 focus:outline-none min-w-0 flex-1 max-w-md" />
          {dirty && (
            <>
              <button onClick={() => renameMutation.mutate()} disabled={!name.trim() || renameMutation.isPending}
                className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs hover:bg-accent-hover transition disabled:opacity-50">
                이름 저장
              </button>
              <button onClick={() => setName(site.name)}
                className="border border-gray-300 text-gray-600 px-3 py-1.5 rounded-lg text-xs hover:bg-gray-50 transition">
                되돌리기
              </button>
            </>
          )}
        </div>
        <button onClick={() => exportSiteXlsx(site)}
          className="border border-gray-300 text-gray-600 px-3 py-1.5 rounded-lg text-xs hover:bg-gray-50 transition shrink-0">
          xlsx 내보내기
        </button>
        <button onClick={() => {
          if (confirm(`'${site.name}' 현장과 소속 설비를 삭제할까요?`))
            deleteMutation.mutate()
        }}
          className="border border-red-200 text-red-600 px-3 py-1.5 rounded-lg text-xs hover:bg-red-50 transition shrink-0">
          현장 삭제
        </button>
      </div>

      <div className="text-xs text-gray-400 mb-4">
        설비 {site.equipments.length}건 · 최근 수신/수정 {new Date(site.updatedAt).toLocaleString('ko-KR')}
      </div>

      <div className="space-y-4">
        {site.equipments.length === 0 && (
          <div className="bg-surface rounded-xl border border-gray-200 p-8 text-center text-gray-400 text-xs">
            이 현장에 설비가 없습니다.
          </div>
        )}
        {site.equipments.map(e => (
          <EquipmentCard key={e.id} equipment={e} siteId={id} />
        ))}
      </div>
    </div>
  )
}
